import { isVisitorSegmentsFeatureEnabled } from '@va/dashboard/selectors/app';
import { getWindowWidth } from '@va/dashboard/selectors/ui';
import { FilterIcon, PencilEditIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { AppliedFilters, StaticLayoutFilterButton, useFiltersContext } from '@va/shared/feature-filters';
import { useConfirmationDialogContext } from '@va/shared/util-confirmation';
import { getUserRole } from '@va/standalone/shared/selectors';
import { UserRole } from '@va/types/website';
import { AccordionSection, Button, Heading5, InputMessage, TextInput, fontWeights } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { is3asApp } from '@va/util/helpers';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import DeleteSegmentsConfirmationDialog from './DeleteSegmentsConfirmationDialog';
import { AaasMinCreditsTooltip, UpgradePlanTooltip } from './UpgradePlanTooltip';
import { useVisitorSegmentsContext } from './VisitorSegmentsContext';
import { RightSideView } from './types';
import { SegmentsFormFields, useSegmentsForm } from './useSegmentsForm';

const CreateEditSection = () => {
  const translate = useTranslate();
  const userRole = useSelector(getUserRole);
  const { rightSideView, segmentToEdit, deleteSegments } = useVisitorSegmentsContext();
  const { values, handleChange, setFieldValue, handleSubmit, errors } = useSegmentsForm();
  const { appliedFilters } = useFiltersContext();
  const windowWidth = useSelector(getWindowWidth);
  const { confirm } = useConfirmationDialogContext();

  const visitorSegmentsEnabled = useSelector(isVisitorSegmentsFeatureEnabled);
  const userIsWatcher = userRole === UserRole.WATCHER;

  const titleBaseKey = useMemo(() => {
    if (rightSideView === RightSideView.CREATE) return 'all.visitorSegments.createSegment';
    return 'all.visitorSegments.editSegment';
  }, [rightSideView]);

  const buttonText = useMemo(
    () =>
      rightSideView === RightSideView.EDIT ? translate('button.updateSegment') : translate('button.createSegment'),
    [rightSideView, translate],
  );

  return (
    <div className='flex shrink-0 w-full xl:w-[40%]'>
      {windowWidth >= 1280 && (
        <div className='shrink-0 bg-gradient-to-b from-gray-mercury h-full w-[0.0625rem] mx-24 !-mt-30' />
      )}
      <form
        className='flex flex-col gap-18px w-full xl:min-w-[25rem]'
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <TooltipWrapper interactive content={translate(`${titleBaseKey}.tooltip`)} trigger='click'>
          <div>
            <Heading5 weight={fontWeights.semibold} className='w-fit underline-dashed cursor-default'>
              {translate(`${titleBaseKey}.title`)}
            </Heading5>
          </div>
        </TooltipWrapper>
        <AccordionSection
          required
          isInitiallyOpen
          title={translate('all.visitorSegments.basicSettings')}
          icon={<PencilEditIcon color='#696969' />}
        >
          <TextInput
            required
            name='name'
            label={translate('labels.segmentName')}
            value={values.name}
            clearField={() => {
              setFieldValue(SegmentsFormFields.name, '');
            }}
            onChange={handleChange}
            error={errors.name}
          />
          <TextInput
            label={translate('labels.segmentNote')}
            name='note'
            value={values.note}
            clearField={() => {
              setFieldValue(SegmentsFormFields.note, '');
            }}
            onChange={handleChange}
          />
        </AccordionSection>

        <AccordionSection
          className='!p-3'
          title={translate('all.visitorSegments.conditions')}
          icon={<FilterIcon color='#696969' />}
          isInitiallyOpen
        >
          <AppliedFilters keepHorizontalDivider={false} className='m-3' />
          {appliedFilters.length === 0 && <StaticLayoutFilterButton />}
        </AccordionSection>
        {errors.conditions && <InputMessage error={errors.conditions as string} />}

        {rightSideView === RightSideView.EDIT && (
          <Button
            disabled={userIsWatcher}
            color='tertiary'
            text={translate('button.deleteSegment')}
            className='text-red-pure'
            tooltip={userIsWatcher ? translate('tooltips.contributorRole') : undefined}
            onClick={() => {
              confirm(() => {
                deleteSegments([segmentToEdit?.id ?? '']);
              }, DeleteSegmentsConfirmationDialog);
            }}
          />
        )}
        <TooltipWrapper
          useDefaultStyle={userIsWatcher}
          disabled={visitorSegmentsEnabled && !userIsWatcher}
          interactive
          content={<SubmitButtonTooltip />}
          arrow={false}
          tooltipClassNames='max-w-[22.5rem]'
        >
          <div>
            <Button
              className='w-full'
              onClick={() => handleSubmit()}
              color='primary'
              text={buttonText}
              disabled={userIsWatcher || !visitorSegmentsEnabled}
            />
          </div>
        </TooltipWrapper>
      </form>
    </div>
  );
};
export default CreateEditSection;

const SubmitButtonTooltip = () => {
  const userRole = useSelector(getUserRole);
  const userIsWatcher = userRole === UserRole.WATCHER;
  const translate = useTranslate();

  if (is3asApp()) {
    return <AaasMinCreditsTooltip />;
  }

  if (userIsWatcher) return translate('tooltips.contributorRole');
  return <UpgradePlanTooltip />;
};
